import { type User, type UserResponse, type AuthResponse } from '@supabase/supabase-js'
import { supabase } from './supabase/client'
import { Platform } from '../utils/platform'

export const authService = {
  // Sign in with email (main method)
  async signInWithEmail(email: string, password: string): Promise<UserResponse> {
    try {
      const response = await supabase.auth.signInWithPassword({
        email,
        password
      });
      
      if (response.error) {
        throw new Error(response.error.message || 'auth.errors.generic');
      }
      
      return response;
    } catch (error: any) {
      console.error('Login error:', error);
      throw new Error(error.message || 'auth.errors.generic');
    }
  },
  
  // Legacy method - alias for signInWithEmail
  async loginWithEmail(email: string, password: string): Promise<UserResponse> {
    return this.signInWithEmail(email, password);
  },

  async loginWithGoogle(): Promise<{ user: User | null }> {
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`
        }
      });
      
      if (error) {
        throw new Error(error.message || 'auth.errors.generic');
      }
      
      // For OAuth providers, this will redirect and not return a user immediately
      // We return a placeholder object to maintain compatibility with existing code
      return { user: null };
    } catch (error: any) {
      console.error('Google login error:', error);
      throw new Error(error.message || 'auth.errors.generic');
    }
  },
  
  async loginWithApple(): Promise<{ user: User | null }> {
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'apple',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`
        }
      });
      
      if (error) {
        throw new Error(error.message || 'auth.errors.generic');
      }
      
      // For OAuth providers, this will redirect and not return a user immediately
      // We return a placeholder object to maintain compatibility with existing code
      return { user: null };
    } catch (error: any) {
      console.error('Apple login error:', error);
      throw new Error(error.message || 'auth.errors.generic');
    }
  },
  
  async signupWithEmail(
    email: string,
    password: string,
    metadata?: { [key: string]: any }
  ): Promise<{ success: boolean; user?: User | null; error?: string }> {
    try {
      console.log(`Attempting to sign up user with email: ${email}`);
      
      // First try to sign in to see if the user already exists
      const { data: signInData } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (signInData?.user) {
        console.log(`User with email ${email} already exists, signing in instead`);
        return { success: true, user: signInData.user };
      }

      // User doesn't exist, proceed with signup
      console.log(`Creating new account for email: ${email}`);
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: metadata,
        },
      });

      if (error) {
        console.error(`Error signing up: ${error.message}`, error);
        return { success: false, error: error.message };
      }

      console.log(`User registered successfully: ${data.user?.id}`);
      return { success: true, user: data.user };
    } catch (error: any) {
      const errorMessage = error instanceof Error 
        ? error.message 
        : 'Unknown error during signup';
      console.error(`Error in signupWithEmail: ${errorMessage}`, error);
      return { success: false, error: errorMessage };
    }
  },
  
  async signInWithMagicLink(
    email: string,
    redirectTo?: string
  ): Promise<{ success: boolean; error?: string }> {
    try {
      console.log(`Sending magic link to: ${email}`);
      
      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: {
          emailRedirectTo: redirectTo,
        },
      });

      if (error) {
        console.error(`Error sending magic link: ${error.message}`, error);
        return { success: false, error: error.message };
      }

      console.log(`Magic link sent successfully to: ${email}`);
      return { success: true };
    } catch (error: any) {
      const errorMessage = error instanceof Error
        ? error.message
        : 'Unknown error sending magic link';
      console.error(`Error in signInWithMagicLink: ${errorMessage}`, error);
      return { success: false, error: errorMessage };
    }
  },
  
  async signUp(email: string, password: string): Promise<AuthResponse> {
    try {
      const response = await supabase.auth.signUp({
        email,
        password
      });
      
      if (response.error) {
        throw new Error(response.error.message || 'auth.errors.generic');
      }
      
      return response;
    } catch (error: any) {
      console.error('Signup error:', error);
      throw new Error(error.message || 'auth.errors.generic');
    }
  },
  
  async signOut(): Promise<void> {
    try {
      const { error } = await supabase.auth.signOut();
      
      if (error) {
        throw new Error(error.message || 'auth.errors.generic');
      }
    } catch (error: any) {
      console.error('Signout error:', error);
      throw new Error(error.message || 'auth.errors.generic');
    }
  },
  
  async resetPassword(email: string): Promise<void> {
    try {
      let options = {};
      
      // Only add redirectTo for web platforms, not for React Native
      if (typeof window !== 'undefined' && !Platform.isNative) {
        options = {
          redirectTo: `${window.location.origin}/reset-password`
        };
      }
      
      const { error } = await supabase.auth.resetPasswordForEmail(email, options);
      
      if (error) {
        throw new Error(error.message || 'auth.errors.generic');
      }
    } catch (error: any) {
      console.error('Reset password error:', error);
      throw new Error(error.message || 'auth.errors.generic');
    }
  },
  
  async getCurrentUser(): Promise<User | null> {
    try {
      const { data, error } = await supabase.auth.getUser();
      
      if (error) {
        throw new Error(error.message || 'auth.errors.generic');
      }
      
      return data.user;
    } catch (error: any) {
      console.error('Get current user error:', error);
      return null;
    }
  }
} 